import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { appRootURL } from "@/constants";
import {
  setShortListTogglePDFStatus,
  updateMapFilters,
} from "@/features/auth/interactiveSurveySlice";
import { MediaFile } from "@/features/media/types/media.types";
import type { Survey } from "@/features/survey/types";
import { Property } from "@/features/survey/types";
import Analytics from "@/services/Analytics";
import {
  useGenerateSurveyPdfMutation,
  useGetPdfUrlQuery,
  useMarketMapCallPollMutation,
} from "@/services/mediaService";
import { useGetSurveyDetailsQuery } from "@/services/survey";
import { EmeraldSwitch } from "@emerald-react/switch";
import { EmeraldTooltip } from "@emerald-react/tooltip";
import moment from "moment";
import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { isPdfFile } from "../utils";
import CircularSpinner from "./CircularSpinner";

// TypeScript interfaces following GitHub Copilot conventions
interface PdfUrlResponse {
  readonly id: string;
  readonly surveyId: string;
  readonly fileUrl: string;
  readonly state: number;
  readonly isShortlisted: boolean;
  readonly createdOn: string;
  readonly updatedOn?: string;
}

interface SurveySection {
  readonly name: string;
  readonly isCustom: boolean;
  readonly isHidden: boolean;
  readonly pdfs: readonly { mediaUrl: string }[];
}

interface SurveySectionPreview {
  readonly surveyId: string;
  readonly name: string;
  readonly isLandscape: boolean;
  readonly sectionInfoList: readonly {
    name: string;
    link: string;
  }[];
  readonly isShortlisted?: boolean;
}

interface PdfDrawerProps {
  readonly isOpen: boolean;
  readonly isShortlistedOnly: boolean;
  readonly onClose: () => void;
  readonly shortListToggleStatus: (value: boolean) => void;
}

// PDF paper size constants following TypeScript conventions
const PDFPaperSize = {
  Landscape: "Landscape",
  Portrait: "Portrait",
} as const;

// Module-level variables for PDF state management
let shortlistPdfCreatedDate = "";
let notShortlistPdfCreatedDate = "";
let shortlistPdfUrl = "";
let notShortlistPdfUrl = "";
let timeOutCount = 0;
const maxCounterTime = 360; // 1 hour timeout
const countTime = 10000; // 10 seconds

/**
 * PDF Drawer Component
 * A right-sliding drawer for PDF report generation options
 */
const PdfDrawer: FC<PdfDrawerProps> = ({
  isOpen,
  isShortlistedOnly,
  onClose,
  shortListToggleStatus,
}) => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const dispatch = useAppDispatch();

  // Get shortListTogglePDFStatus from Redux state
  const { shortListTogglePDFStatus } = useAppSelector(
    (state) => state.interactiveSurvey.mapFilters,
  );
  const { currentSurvey } = useAppSelector((state) => state.interactiveSurvey);
  const { mediaFiles } = useAppSelector((state) => state.media);
  const [generatePDFText, setGeneratePDFText] = useState(false);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [pdfUpdate, setPdfUpdate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [surveyUpdate, setSurveyUpdate] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [pdfArr, setPdfArr] = useState<PdfUrlResponse[]>([]);
  const [pollResult, setPollResult] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const shortlistMessage = `Once you begin shortlisting you can filter your view to show
                    only shortlisted properties. You can review these with your brokerage team.`;

  // Sync with sessionStorage on component mount
  useEffect(() => {
    const shortlisted = sessionStorage.getItem("shortlisted");
    if (
      shortlisted !== null &&
      JSON.parse(shortlisted) !== shortListTogglePDFStatus
    ) {
      dispatch(setShortListTogglePDFStatus(JSON.parse(shortlisted)));
    }
  }, [dispatch, shortListTogglePDFStatus]);

  // RTK Query hooks following GitHub Copilot conventions
  const [marketMapCallPoll] = useMarketMapCallPollMutation();
  const [generateSurveyPdf] = useGenerateSurveyPdfMutation();

  // Fetch survey details using RTK Query
  const { data: surveyData, refetch: refetchSurveyData } =
    useGetSurveyDetailsQuery(surveyId!, {
      skip: !surveyId || !isOpen, // Only fetch when drawer is open and surveyId exists
      refetchOnMountOrArgChange: true,
    });

  const { data: pdfUrlData, refetch: refetchPdfUrl } = useGetPdfUrlQuery(
    surveyId!,
    {
      skip: !surveyId || !isOpen, // Only fetch when drawer is open and surveyId exists
      refetchOnMountOrArgChange: true,
    },
  );

  // Check if shortlisting should be disabled (no shortlisted properties)
  const shortlistedDisabled = (): boolean => {
    if (!surveyData?.properties) return true;
    return (
      surveyData.properties.filter((item: Property) => item.isClientShortlist)
        .length === 0
    );
  };

  // Toggle shortlisted status with sessionStorage and PDF status management
  const toggleShortlisted = (): void => {
    const newValue = !isShortlistedOnly;
    window.sessionStorage.setItem("shortlisted", JSON.stringify(newValue));
    dispatch(updateMapFilters({ showShortlistOnly: newValue }));
    dispatch(setShortListTogglePDFStatus(newValue));
    shortListToggleStatus(newValue);

    if (isOpen) {
      dispatch(setShortListTogglePDFStatus(true));

      // Check if we can reuse existing PDF when switching from shortlisted to non-shortlisted
      if (
        !newValue && // Switching to non-shortlisted
        notShortlistPdfUrl && // Non-shortlisted PDF exists
        notShortlistPdfCreatedDate && // Has creation date
        surveyData // Survey data is available
      ) {
        const surveyUpdateOn = moment(surveyData.updatedOn || "");
        const nonShortlistPdfCreatedOn = moment(notShortlistPdfCreatedDate);

        // If survey hasn't been updated since the non-shortlisted PDF was created, reuse it
        if (
          surveyUpdateOn.diff(nonShortlistPdfCreatedOn, "milliseconds") <= 0
        ) {
          // Reuse existing non-shortlisted PDF
          setOpenErrorModal(false);
          setGeneratePDFText(false);
          setLoading(false);
          setPdfUpdate(false);
          setPdfAvailable(true);
          setInitialDataLoaded(true);
          setSurveyUpdate(false);
          return; // Exit early, don't regenerate
        }
      }

      // Check if we can reuse existing PDF when switching from non-shortlisted to shortlisted
      if (
        newValue && // Switching to shortlisted
        shortlistPdfUrl && // Shortlisted PDF exists
        shortlistPdfCreatedDate && // Has creation date
        surveyData // Survey data is available
      ) {
        const surveyUpdateOn = moment(surveyData.updatedOn || "");
        const shortlistPdfCreatedOn = moment(shortlistPdfCreatedDate);

        // If survey hasn't been updated since the shortlisted PDF was created, reuse it
        if (surveyUpdateOn.diff(shortlistPdfCreatedOn, "milliseconds") <= 0) {
          // Reuse existing shortlisted PDF
          setOpenErrorModal(false);
          setGeneratePDFText(false);
          setLoading(false);
          setPdfUpdate(false);
          setPdfAvailable(true);
          setInitialDataLoaded(true);
          setSurveyUpdate(false);
          return; // Exit early, don't regenerate
        }
      }

      // Trigger PDF generation process with loading UI
      setOpenErrorModal(false);
      setGeneratePDFText(true);
      setLoading(true);
      setPdfUpdate(true);
      setPdfAvailable(false);
      setInitialDataLoaded(false);

      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.location.reload(); // Forces a reload from the server
      }

      // Start the PDF generation process
      handleDownloadSurveyPDF();
    }
  };

  const handleDownloadSurveyPDF = async () => {
    if (!loading) {
      setOpenErrorModal(false);
      setGeneratePDFText(true);
      setLoading(true);
      setPdfUpdate(true);
      setTimeout(
        () => {
          pollCall(); // Polling call for market map image generation
        },
        shortListTogglePDFStatus ? 30000 : 6000,
      ); // 6 second
    }
  };

  useEffect(() => {
    if (surveyId !== undefined && !pdfUpdate && isOpen) {
      setPollResult(false);
      getPdfUrlHandle();
    }
  }, [shortListToggleStatus, isOpen]);

  useEffect(() => {
    if (
      ((pdfAvailable && surveyUpdate) || !pdfArr.length) &&
      initialDataLoaded &&
      !generatePDFText &&
      !pollResult
    ) {
      setLoading(true);
      setPdfAvailable(false);
      handleDownloadSurveyPDF();
    }
  }, [
    pdfAvailable,
    surveyUpdate,
    initialDataLoaded,
    generatePDFText,
    handleDownloadSurveyPDF,
    pdfArr.length,
    pollResult,
  ]);

  const handleUpdatedSurveyPDF = async () => {
    setGeneratePDFText(false);
    setInitialDataLoaded(true);
    setPdfAvailable(true);

    // Always try to refetch to get the latest survey data
    let surveyResponse = surveyData;
    if (surveyId) {
      try {
        const { data: fetchedData } = await refetchSurveyData();
        surveyResponse = fetchedData;
      } catch (error) {
        console.warn(
          "Could not refetch survey data, using existing data:",
          error,
        );
        surveyResponse = surveyData;
      }
    }

    if (surveyResponse) {
      const surveyUpdateOn = moment(surveyResponse.updatedOn || "");
      // Need to implementement the logic for both shortlisted and not shortlisted pdf update check
      if (
        (isShortlistedOnly === true &&
          (shortlistPdfUrl === "" ||
            surveyUpdateOn.diff(shortlistPdfCreatedDate, "milliseconds") >
              0)) ||
        (isShortlistedOnly === false &&
          (notShortlistPdfUrl === "" ||
            surveyUpdateOn.diff(notShortlistPdfCreatedDate, "milliseconds") >
              0))
      ) {
        setSurveyUpdate(true);
      } else {
        setSurveyUpdate(false);
        setLoading(false);
        setGeneratePDFText(false);
        setInitialDataLoaded(true);
        setPdfAvailable(true);
      }
    }
    setOpenErrorModal(false);
    setLoading(false);
    setPdfUpdate(false);
  };

  const errorStateManagement = () => {
    setLoading(false);
    setOpenErrorModal(true);
    setPdfAvailable(false);
    setGeneratePDFText(false);
    setInitialDataLoaded(true);
  };

  const getPdfUrlHandle = async () => {
    // Only proceed if drawer is open to avoid refetch errors
    if (!isOpen) {
      return;
    }

    try {
      // Always fetch latest data instead of using cached data
      let currentPdfUrlData = pdfUrlData;

      // Always refetch to get the latest PDF URL data when drawer is open
      if (isOpen && surveyId) {
        try {
          const { data: fetchedData } = await refetchPdfUrl();
          currentPdfUrlData = fetchedData;
        } catch (refetchError) {
          console.warn("Could not refetch PDF URL data:", refetchError);
          // Fallback to cached data if refetch fails
          currentPdfUrlData = pdfUrlData;
        }
      }
      if (currentPdfUrlData !== undefined && currentPdfUrlData.length > 0) {
        setPdfArr(currentPdfUrlData);
        if (
          currentPdfUrlData &&
          currentPdfUrlData.length > 0 &&
          currentPdfUrlData[0] !== null &&
          currentPdfUrlData[0].state === 3
        ) {
          const shortlistedRes = currentPdfUrlData.filter(
            (pdf: any) => pdf.isShortlisted === true,
          );
          const notShortlistedRes = currentPdfUrlData.filter(
            (pdf: any) => pdf.isShortlisted === false,
          );
          if (shortlistedRes.length) {
            shortlistPdfUrl = shortlistedRes[0].fileUrl;
            shortlistPdfCreatedDate = shortlistedRes[0].createdOn;
          }
          if (notShortlistedRes.length) {
            notShortlistPdfUrl = notShortlistedRes[0].fileUrl;
            notShortlistPdfCreatedDate = notShortlistedRes[0].createdOn;
          }
          handleUpdatedSurveyPDF();
        } else if (
          currentPdfUrlData[0] !== null &&
          currentPdfUrlData[0].state === 4
        ) {
          setPollResult(false);
          setPdfUpdate(false);
          errorStateManagement();
        } else {
          if (
            currentPdfUrlData[0] !== null &&
            currentPdfUrlData[0].state !== 3
          ) {
            setGeneratePDFText(true);
            setOpenErrorModal(false);
            setPdfAvailable(false);
            setPollResult(false);
            setPdfUpdate(false);
          }
          timeOutCount += 1;
          setTimeout(() => {
            if (timeOutCount < maxCounterTime) {
              getPdfUrlHandle();
            } else {
              errorStateManagement();
              setPollResult(false);
              setPdfUpdate(false);
            }
          }, countTime);
        }
      } else {
        setPdfAvailable(false);
        setInitialDataLoaded(true);
      }
    } catch (error) {
      console.error("Error in getPdfUrlHandle:", error);
      errorStateManagement();
    }
  };

  const handleOpenSurveyPDF = () => {
    if (pdfAvailable && surveyId !== undefined) {
      Analytics.send({
        event: "clientPDFOpen",
        user_name: localStorage.getItem("displayName"),
        survey_name: surveyData?.name,
        client_name: surveyData?.clientName,
        survey_id: surveyData?.id,
        project_id: surveyData?.projectId,
      });
      const url =
        isShortlistedOnly === true ? shortlistPdfUrl : notShortlistPdfUrl;
      window.open(url, "_blank");
    }
  };

  let interval: any;
  const pollCall = () => {
    if (!pollResult) {
      interval = setInterval(() => {
        marketMapPollFunction();
      }, 10000);
    }
  };

  /** Creates sections for property media (images, floor plans, flyers) */
  const generatePropertyMediaSections = (
    survey: Survey,
    domain: string,
    surveyId: string | undefined,
    propertyId: string,
    mediaFiles: {
      images?: MediaFile[];
      floorPlans?: MediaFile[];
      flyers?: MediaFile[];
    },
  ): { name: string; link: string }[] => {
    const links: { name: string; link: string }[] = [];

    // Generate floor plan sections
    mediaFiles.floorPlans?.forEach((floorPlan, index) => {
      if (floorPlan.isHidden || !survey?.isIncludeSitePlans) return;
      const link =
        isPdfFile(floorPlan) && floorPlan.cloudfrontUrl
          ? floorPlan.cloudfrontUrl
          : `${domain}/${surveyId}/preview/properties/${propertyId}/floorPlanImage/${index + 1}`;
      links.push({ name: "property floorplan", link });
    });

    // Generate flyer sections - filter out source PDFs if modified versions exist
    if (mediaFiles.flyers && survey?.isIncludeFlyers) {
      // Create a set of source media IDs that have been modified
      const modifiedMediaIds = new Set(
        mediaFiles.flyers
          .filter((file) => file.modifiedFromMediaId)
          .map((file) => file.modifiedFromMediaId),
      );

      // Only include flyers that are not hidden, have a URL, and are not source PDFs
      mediaFiles.flyers.forEach((flyer) => {
        if (
          !flyer.isHidden &&
          flyer.cloudfrontUrl &&
          !modifiedMediaIds.has(flyer.mediaId)
        ) {
          links.push({ name: "property flyer", link: flyer.cloudfrontUrl });
        }
      });
    }

    return links;
  };

  // Named function to get floorplan property links following async/await pattern
  const getPropertyLinks = async (
    property: Property,
    isClientShortList = false,
  ): Promise<{ name: string; link: string }[]> => {
    const links: { name: string; link: string }[] = [];

    const propertyLink = {
      name: property.name || property.propertyName || "Property",
      link: `${appRootURL}/${surveyId}/preview/properties/${property.id}${isClientShortList ? "?isClientShortList=true" : ""}`,
    };
    links.push(propertyLink);
    return links;
  };

  // Named function to get all property links following async/await pattern
  const getAllPropertyLinks = async (
    properties: Property[],
    isClientShortList = false,
  ): Promise<{ name: string; link: string }[]> => {
    if (!surveyData || !properties) {
      return [];
    }

    // TODO: Update property filtering when survey data structure is available
    const filteredProperties = properties.filter((item) => !item.isHidden);

    try {
      const allLinks: { name: string; link: string }[] = [];

      for (const property of filteredProperties) {
        const propertyMedia = mediaFiles[property.id];
        const propertyLinks = await getPropertyLinks(
          property,
          isClientShortList,
        );
        const mediaLinks = await generatePropertyMediaSections(
          surveyData,
          appRootURL,
          surveyId,
          property.id,
          {
            images: propertyMedia?.images,
            floorPlans: propertyMedia?.floorPlans,
            flyers: propertyMedia?.fliers,
          },
        );
        console.log(mediaLinks, "media links");
        allLinks.push(...propertyLinks, ...mediaLinks);
      }

      return allLinks;
    } catch (error) {
      console.error("Error getting all property links:", error);
      return [];
    }
  };

  // Named function to get section link following TypeScript conventions
  const getSectionLink = (
    surveyId: string,
    section: SurveySection,
    isClientShortList = false,
  ): string => {
    const baseLink = `${appRootURL}/${surveyId}/preview/${section.name.replace(/\s/g, "")}`;
    return isClientShortList ? `${baseLink}?isClientShortList=true` : baseLink;
  };

  // Named function to generate survey PDF following async/await pattern
  const generateSurveyPDF = async (surveyDataInput: any): Promise<void> => {
    const isShortlistedData = sessionStorage.getItem("shortlisted") === "true";
    if (!surveyDataInput || !surveyId) {
      throw new Error("Survey data and surveyId are required");
    }
    try {
      const isClientShortList =
        sessionStorage.getItem("shortlisted") === "true";
      const surveyDataCopy = { ...surveyDataInput };

      // Filter properties based on visibility and shortlist status
      surveyDataCopy.properties = surveyDataCopy.properties
        .filter((item: any) => !item.isHidden)
        .filter((item: any) => !isClientShortList || item.isClientShortlist);

      const propertySection: SurveySectionPreview = {
        isLandscape: surveyDataCopy.pdfPaperSize === PDFPaperSize.Landscape,
        name: surveyDataCopy.name.replace(/[^a-zA-Z0-9.\-_ ]/g, ""),
        sectionInfoList: surveyDataCopy.sections
          .filter(
            (item: any) =>
              (!item.isCustom && !item.isHidden) ||
              (item.isCustom && !item.isHidden && item.pdfs?.length > 0),
          )
          .map((section: any) => {
            if (!section.isCustom) {
              return {
                name: section.name,
                link: getSectionLink(
                  surveyDataCopy.id,
                  section,
                  isClientShortList,
                ),
              };
            }
            return {
              name: "section flyer",
              link: section.pdfs[0].mediaUrl,
            };
          }),
        surveyId: surveyDataCopy.id,
        isShortlisted: isClientShortList,
      };

      const propertiesIndex = propertySection.sectionInfoList.findIndex(
        (item) => item.name === "properties",
      );

      if (propertiesIndex !== -1) {
        const propertyLinks = await getAllPropertyLinks(
          surveyDataCopy.properties,
          isClientShortList,
        );
        const chunk1 = propertySection.sectionInfoList.slice(
          0,
          propertiesIndex,
        );
        const chunk2 = propertySection.sectionInfoList.slice(
          propertiesIndex + 1,
        );

        // Create new section info list with property links
        const newSectionInfoList = [...chunk1, ...propertyLinks, ...chunk2];
        const updatedPropertySection = {
          ...propertySection,
          sectionInfoList: newSectionInfoList,
        };

        // Call actual PDF generation service using RTK Query mutation
        const result = await generateSurveyPdf({
          surveyId: surveyId,
          name: updatedPropertySection.name,
          isLandscape: updatedPropertySection.isLandscape,
          sectionInfoList: updatedPropertySection.sectionInfoList,
          isShortlisted: isShortlistedData,
        }).unwrap();

        // Handle successful PDF generation response
        if (!result) {
          throw new Error(result || "PDF generation failed");
        }
      } else {
        // No properties section found, generate PDF with available sections
        await generateSurveyPdf({
          surveyId: surveyId,
          name: propertySection.name,
          isLandscape: propertySection.isLandscape,
          sectionInfoList: propertySection.sectionInfoList,
          isShortlisted: isShortlistedData,
        }).unwrap();
      }
    } catch (error) {
      console.error("Error generating survey PDF:", error);
      setOpenErrorModal(true);
      setGeneratePDFText(false);
      setLoading(false);
      throw error;
    }
  };

  const generatePDF = async () => {
    try {
      const { data: surveyResponse } = await refetchSurveyData();
      if (!surveyResponse) {
        console.error("No survey data available for PDF generation");
        setOpenErrorModal(true);
        setGeneratePDFText(false);
        setLoading(false);
        return;
      }
      await generateSurveyPDF(surveyResponse);
    } catch (error) {
      console.error("Error in generatePDF:", error);
      setOpenErrorModal(true);
    } finally {
      setTimeout(() => {
        getPdfUrlHandle();
      }, 10000); // 10 second
    }
  };

  const marketMapPollFunction = async () => {
    try {
      const response = await marketMapCallPoll(surveyId || "");
      if (response.error) {
        const result = "data" in response.error ? response.error.data : null;
        if (typeof result === "string" && result !== null) {
          if (result === "success") {
            setShortListTogglePDFStatus(false);
            setPollResult(true);
            clearInterval(interval);
            generatePDF();
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Handle keyboard events for drawer
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ zIndex: 151 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onClose();
          }
        }}
        aria-label="Close drawer"
      />

      {/* Drawer Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transform transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <p
            style={{
              color: "var(--color-default)",
              fontWeight: 200,
              fontSize: "20px",
            }}
          >
            Generate PDF Report
          </p>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-md"
            aria-label="Close drawer"
            type="button"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col p-6 gap-y-4">
          {/* Toggle Section */}

          {currentSurvey?.isSurveyShortlistEnabled !== false && (
            <div className="flex items-center space-x-3">
              <EmeraldTooltip
                triggerElement={
                  <span
                    className="info-i"
                    style={{ margin: "0 -4px 12px -2px" }}
                    aria-label={shortlistMessage}
                  >
                    i
                  </span>
                }
              >
                {shortlistMessage}
              </EmeraldTooltip>
              <span className="shortlist-toggle-label">Shortlisted</span>
              <EmeraldSwitch
                selected={isShortlistedOnly}
                onChange={toggleShortlisted}
                className="shortlist-toggle-switch"
                disabled={loading || generatePDFText || shortlistedDisabled()}
              />
            </div>
          )}

          {/* Generate Button */}
          {pdfAvailable && timeOutCount < maxCounterTime && (
            <>
              <button
                onClick={() => {
                  handleOpenSurveyPDF();
                }}
                className="w-full bg-primary text-white font-medium py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                style={{ backgroundColor: "var(--color-cbre-green-button)" }}
                type="button"
              >
                Open PDF Report
              </button>

              {/* Help Text */}
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Click Open PDF report to view, download or print your PDF
                report. If changes are made to the survey, you will be prompted
                to generate a new PDF.
              </p>
            </>
          )}

          {initialDataLoaded &&
            !loading &&
            !pdfAvailable &&
            !generatePDFText && (
              <>
                <button
                  onClick={handleDownloadSurveyPDF}
                  className="w-full bg-primary text-white font-medium py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  type="button"
                >
                  {openErrorModal && <>Retry</>}
                </button>

                {/* Help Text */}
                <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
                  Generate a PDF report of this survey. Your report will include
                  the latest content currently visible in this survey.
                </p>
              </>
            )}

          {generatePDFText && !openErrorModal && !pdfAvailable && (
            <>
              <CircularSpinner size="medium" color="primary" />
              {/* Help Text */}
              <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
                Your PDF report is generating. Once ready, you can open the link
                to the report in your browser to view or print.
              </p>
            </>
          )}
        </div>
      </div>
      <iframe
        ref={iframeRef}
        src={`/interactive/${surveyId}/marketmapRendered${isShortlistedOnly ? "?isClientShortList=true" : ""}`}
        title="Interactive Survey Market Map"
        style={{ width: "100%", height: "100%", visibility: "hidden" }}
      />
    </div>
  );
};

export default PdfDrawer;
